/**
 * The official Telegram Login widget, mounted into a container this component owns.
 *
 * The widget is a script that reads its options from `data-*` attributes and calls a
 * named global function with the signed user object. Each mount gets its own callback
 * name, set on `window` while mounted and removed on unmount, so a screen that is
 * hidden never leaves a live callback behind.
 *
 * `onAuth` is read through a ref: the widget keeps the callback it was given at mount,
 * and the parent may re-render with a fresh closure in the meantime.
 */
import { useEffect, useRef, type JSX } from "react";

import type { TelegramWidgetUser } from "@/api/auth";

const TELEGRAM_WIDGET_SRC = "https://telegram.org/js/telegram-widget.js?22";

export type TelegramCallbackName = "kapitanTelegramLogin" | "kapitanTelegramConnect";

type TelegramCallback = (user: TelegramWidgetUser) => void;
type TelegramWindow = Window & { [K in TelegramCallbackName]?: TelegramCallback | undefined };

export interface TelegramWidgetProps {
  botUsername: string;
  callbackName: TelegramCallbackName;
  onAuth: (user: TelegramWidgetUser) => void;
}

export function TelegramWidget({ botUsername, callbackName, onAuth }: TelegramWidgetProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onAuthRef = useRef(onAuth);

  useEffect(() => {
    onAuthRef.current = onAuth;
  }, [onAuth]);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) {
      return undefined;
    }
    // Under React StrictMode an effect runs twice on the same DOM node; clearing first
    // stops the second run from stacking a second widget beside the first.
    container.replaceChildren();

    const target = window as TelegramWindow;
    target[callbackName] = (user: TelegramWidgetUser): void => {
      onAuthRef.current(user);
    };

    const script = document.createElement("script");
    script.async = true;
    script.src = TELEGRAM_WIDGET_SRC;
    script.dataset.telegramLogin = botUsername;
    script.dataset.size = "medium";
    script.dataset.requestAccess = "write";
    script.dataset.onauth = `${callbackName}(user)`;
    container.appendChild(script);

    return (): void => {
      target[callbackName] = undefined;
      container.replaceChildren();
    };
  }, [botUsername, callbackName]);

  return <div ref={containerRef} className="flex min-h-10 justify-center" />;
}
